import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { ScrollArea } from "@/components/ui/scroll-area";
import { 
  Menu, 
  User, 
  Settings, 
  Image, 
  Package, 
  Heart, 
  ShoppingBag, 
  Shield, 
  BarChart3, 
  LogOut,
  Palette
} from "lucide-react";
import { NavbarIcons } from "./NavbarIcons";
import { useTranslation } from "react-i18next";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";

interface MobileNavProps {
  user: any;
  showArtistContent: boolean;
  showBuyerContent: boolean;
  isAdmin: boolean;
  mobileMenuOpen: boolean;
  onMobileMenuOpenChange: (open: boolean) => void;
  handleSignOut: () => void;
}

export const MobileNav = ({
  user,
  showArtistContent,
  showBuyerContent,
  isAdmin,
  mobileMenuOpen,
  onMobileMenuOpenChange,
  handleSignOut,
}: MobileNavProps) => {
  const { t } = useTranslation('common');
  
  return (
    <div className="flex lg:hidden items-center gap-2">
      <NavbarIcons user={user} />
      
      <LanguageSwitcher />
      
      <Sheet open={mobileMenuOpen} onOpenChange={onMobileMenuOpenChange}>
        <SheetTrigger asChild>
          <Button variant="ghost" size="icon">
            <Menu className="h-5 w-5" />
          </Button>
        </SheetTrigger>
        <SheetContent side="right" className="w-[300px] sm:w-[400px]">
          <ScrollArea className="h-full pr-4">
            <nav className="flex flex-col gap-4 mt-6 pb-20">
              <Link 
                to="/explore" 
                onClick={() => onMobileMenuOpenChange(false)}
                className="flex items-center gap-2 text-base font-medium py-2 transition-colors hover:text-primary"
              >
                <Palette className="h-5 w-5" />
                {t('nav.allArtworks')}
              </Link>
              <Link 
                to="/explore?type=original" 
                onClick={() => onMobileMenuOpenChange(false)}
                className="flex items-center gap-2 text-base font-medium py-2 transition-colors hover:text-primary"
              >
                {t('nav.originals')}
              </Link>
              <Link 
                to="/explore?type=print" 
                onClick={() => onMobileMenuOpenChange(false)}
                className="flex items-center gap-2 text-base font-medium py-2 transition-colors hover:text-primary"
              >
                {t('nav.prints')}
              </Link>
              <Link 
                to="/artists"
                onClick={() => onMobileMenuOpenChange(false)}
                className="flex items-center gap-2 text-base font-medium py-2 transition-colors hover:text-primary"
              >
                {t('nav.artists')}
              </Link>
              <Link 
                to="/explore?auction=true"
                onClick={() => onMobileMenuOpenChange(false)}
                className="flex items-center gap-2 text-base font-medium py-2 transition-colors hover:text-primary"
              >
                {t('nav.auctions')}
              </Link>
              <Link 
                to="/supplies"
                onClick={() => onMobileMenuOpenChange(false)}
                className="flex items-center gap-2 text-base font-medium py-2 transition-colors hover:text-primary"
              >
                {t('nav.supplies')}
              </Link>
              
              <div className="border-t border-border mt-2" />
              
              {user ? (
                <>
                  {showArtistContent && (
                    <Link to="/upload" onClick={() => onMobileMenuOpenChange(false)}>
                      <Button className="w-full">
                        <Package className="h-5 w-5 mr-2" />
                        {t('nav.sellNow')}
                      </Button>
                    </Link>
                  )}
                  
                  <Link to="/profile" onClick={() => onMobileMenuOpenChange(false)}>
                    <Button variant="ghost" className="w-full justify-start">
                      <User className="h-5 w-5 mr-2" />
                      {t('nav.profile')}
                    </Button>
                  </Link>
                  <Link to="/settings" onClick={() => onMobileMenuOpenChange(false)}>
                    <Button variant="ghost" className="w-full justify-start">
                      <Settings className="h-5 w-5 mr-2" />
                      {t('nav.accountSettings')}
                    </Button>
                  </Link>
                  {showArtistContent && (
                    <>
                      <Link to="/my-artworks" onClick={() => onMobileMenuOpenChange(false)}>
                        <Button variant="ghost" className="w-full justify-start">
                          <Image className="h-5 w-5 mr-2" />
                          {t('nav.myArtworks')}
                        </Button>
                      </Link>
                      <Link to="/artist-orders" onClick={() => onMobileMenuOpenChange(false)}>
                        <Button variant="ghost" className="w-full justify-start">
                          <Package className="h-5 w-5 mr-2" />
                          {t('nav.sales')}
                        </Button>
                      </Link>
                    </>
                  )}
                  {showBuyerContent && (
                    <>
                      <Link to="/favorites" onClick={() => onMobileMenuOpenChange(false)}>
                        <Button variant="ghost" className="w-full justify-start">
                          <Heart className="h-5 w-5 mr-2" />
                          {t('nav.favorites')}
                        </Button>
                      </Link>
                      <Link to="/orders" onClick={() => onMobileMenuOpenChange(false)}>
                        <Button variant="ghost" className="w-full justify-start">
                          <ShoppingBag className="h-5 w-5 mr-2" />
                          {t('nav.orders')}
                        </Button>
                      </Link>
                    </>
                  )}
                  {isAdmin && (
                    <Link to="/admin" onClick={() => onMobileMenuOpenChange(false)}>
                      <Button variant="ghost" className="w-full justify-start">
                        <Shield className="h-5 w-5 mr-2" />
                        {t('nav.admin')}
                      </Button>
                    </Link>
                  )}
                  {showArtistContent && (
                    <Link to="/artist-dashboard" onClick={() => onMobileMenuOpenChange(false)}>
                      <Button variant="ghost" className="w-full justify-start">
                        <BarChart3 className="h-5 w-5 mr-2" />
                        {t('nav.artistDashboard')}
                      </Button>
                    </Link>
                  )}
                  <Button
                    variant="ghost" 
                    onClick={() => {
                      handleSignOut();
                      onMobileMenuOpenChange(false);
                    }} 
                    className="w-full justify-start"
                  >
                    <LogOut className="h-5 w-5 mr-2" />
                    {t('nav.signOut')}
                  </Button>
                </>
              ) : (
                <Link to="/auth" onClick={() => onMobileMenuOpenChange(false)}>
                  <Button className="w-full">{t('nav.registerLogin')}</Button>
                </Link>
              )}
            </nav>
          </ScrollArea>
        </SheetContent>
      </Sheet>
    </div>
  );
};
